import type { ComparisonTheme } from './comparisonVisualThemes';

/** Same as Navbar — full-color mark; shown on white inside the branded header cell for contrast */
const GEOFLEET_LOGO_SVG = 'https://cdn.geofleet.ai/images/logo.svg';

export interface ComparisonRow {
  feature: string;
  geofleet: string;
  competitor: string;
}

export interface ComparisonTableProps {
  competitorLabel: string;
  rows: ComparisonRow[];
  theme: ComparisonTheme;
  id?: string;
}

export default function ComparisonTable({
  competitorLabel,
  rows,
  theme,
  id = 'comparison-table',
}: ComparisonTableProps) {
  return (
    <div className="group/table relative">
      {/* Phones: one card per capability instead of a sideways-scrolling table */}
      <ul className="m-0 list-none space-y-3 p-0 md:hidden" aria-label={`Feature comparison between Geofleet and ${competitorLabel}`}>
        {rows.map((row) => (
          <li key={row.feature} className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm">
            <p className="border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-[13px] font-semibold text-slate-900">{row.feature}</p>
            <div className="px-4 py-3" style={{ backgroundColor: `rgba(${theme.primaryRgb}, 0.06)` }}>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: theme.primary }}>
                Geofleet
              </p>
              <p className="mt-1 flex gap-2 text-[13px] leading-relaxed text-slate-700">
                <i className="ri-checkbox-circle-fill mt-0.5 flex-shrink-0 text-base" style={{ color: theme.primary }} aria-hidden />
                <span>{row.geofleet}</span>
              </p>
            </div>
            <div className="border-t border-slate-100 px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{competitorLabel}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{row.competitor}</p>
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto rounded-2xl border md:block border-slate-200/90 bg-white shadow-md shadow-slate-900/[0.04] ring-1 ring-slate-900/[0.03] scroll-smooth overscroll-x-contain">
        <table className="w-full min-w-[640px] text-left text-sm border-collapse" id={id}>
          <caption className="sr-only">
            Feature comparison between Geofleet and {competitorLabel}
          </caption>
          <thead>
            <tr className="border-b border-slate-200/90">
              <th
                scope="col"
                className="px-5 py-4 font-semibold text-slate-500 text-xs uppercase tracking-wider w-[26%] bg-slate-50 border-b border-slate-200/90 first:rounded-tl-2xl"
              >
                Capability
              </th>
              <th
                scope="col"
                className="px-4 sm:px-5 py-4 w-[37%] align-middle border-b border-slate-200/90 bg-slate-50"
                style={{
                  boxShadow: `inset 3px 0 0 0 ${theme.primary}`,
                }}
              >
                <span className="inline-flex items-center">
                  <img
                    src={GEOFLEET_LOGO_SVG}
                    alt=""
                    className="h-8 w-auto max-w-[min(160px,100%)] object-contain object-left"
                    width={160}
                    height={32}
                  />
                </span>
              </th>
              <th
                scope="col"
                className="px-5 py-4 text-base sm:text-lg font-bold text-slate-900 w-[37%] bg-slate-50 align-middle border-b border-slate-200/90 last:rounded-tr-2xl"
              >
                <span className="inline-flex items-center gap-2.5 min-w-0">
                  <span
                    className="hidden sm:inline-flex h-2 w-2 rounded-full shrink-0 ring-2 ring-slate-200/90"
                    style={{ backgroundColor: theme.primary }}
                    aria-hidden
                  />
                  <span className="min-w-0 leading-snug text-balance">{competitorLabel}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.feature}
                className={`border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50/90 ${
                  i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                }`}
              >
                <th
                  scope="row"
                  className="px-5 py-4 font-medium text-slate-900 align-top text-[13px] leading-snug"
                >
                  {row.feature}
                </th>
                <td
                  className="px-5 py-4 text-slate-700 align-top text-[13px] leading-relaxed border-l border-slate-100/70"
                  style={{ backgroundColor: `rgba(${theme.primaryRgb}, 0.06)` }}
                >
                  <span className="flex gap-2.5">
                    <i
                      className="ri-checkbox-circle-fill flex-shrink-0 mt-0.5 text-base"
                      style={{ color: theme.primary }}
                      aria-hidden
                    />
                    <span>{row.geofleet}</span>
                  </span>
                </td>
                <td className="px-5 py-4 text-slate-600 align-top text-[13px] leading-relaxed border-l border-slate-200/60">
                  {row.competitor}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
